import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'sonner';
import { Check, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useDeleteAttachmentMutation } from '@/redux/attachment/attachment.api.slice';
import {
  selectV2,
  addFiles,
  clearFiles,
  removeFile,
  restoreFile,
  setThumbnailFileId,
  setParsing,
  setParseProgress,
  setParsedReady,
  setExtractedData,
  setCamExtracted,
  setCamExtractedFileId,
  setCamParsing,
  setStep,
  setFileToolpaths,
  setFileExtracted,
  sortGcodeByCutOrder,
  updateFileController,
} from '../v2Upload.slice';
import { parseGcodeInWorker } from '@/workers/useGcodeParser';
import { parseCamInWorker } from '@/workers/useCamParser';
import { isCamFile } from '@/workers/camFiles';
import { camDataFor, chooseCam, ParsedCam } from '../utils/camChoice';
import {
  kindOf,
  controllerOf,
  fmtSize,
  nextId,
  stripControllerPrefix,
  ACCEPT_BY_KIND,
  CONTROLLERS,
  formatList,
} from '../utils/fileHelpers';
import { ImageGrid, type SectionAddProps } from '../components/FileGrids';
import DocLists from '../components/DocLists';
import CamDetailModal from '../components/CamDetailModal';
import { AcceptedFormatsCluster } from '../components/EmptySectionArt';
import ControllerColumns from '../components/ControllerColumns';
import WizardFooter from '../components/WizardFooter';
import VideoLinks from '../components/VideoLinks';
import useFieldErrors from '../useFieldErrors';
import { missingForFiles, Missing } from '../utils/validation';
import { primaryGcodeParse } from '../utils/mergeSources';
import useDraftSync from '../useDraftSync';
import { V2File, V2Operation } from '../v2Upload.d';
import { STEP_HEADING } from '../components/fileCardStyles';

/** Thumbnail selection after a drop: keeps the existing pick if there is
 * one; otherwise falls back to the first newly-dropped image. Pure, so the
 * caller can dispatch it unconditionally instead of branching. */
function nextThumbnailId(
  newFiles: { id: string; kind: 'image' | 'gcode' | 'doc' }[],
  currentThumbnailId: string | null
): string | null {
  if (currentThumbnailId) return currentThumbnailId;
  const firstImage = newFiles.find((f) => f.kind === 'image');
  return firstImage ? firstImage.id : null;
}

/** A worker that never replies (a chunk 404 after a deploy with the tab open,
 * or an error thrown outside the worker's own onmessage try/catch) leaves its
 * promise pending forever — nothing but a timeout can unstick that. 60s is
 * generous for even a large production .art (tens of MB); the ~700 KB test
 * fixture parses in milliseconds, so in the normal case this never fires and
 * only ever cuts off a genuinely wedged worker. Resolving `null` on timeout,
 * rather than rejecting, matches parseCamDrop's own contract: an unknown
 * failure mode must never block the flow, same as a corrupt file. */
const CAM_PARSE_TIMEOUT_MS = 60_000;

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | null> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      () => {
        clearTimeout(timer);
        resolve(null);
      }
    );
  });
}

/** Parses one CAM file. Own try/catch so a corrupt or unreadable file
 * contributes nothing rather than throwing; null when the parse failed or
 * timed out. */
async function parseCamFile(
  camFile: V2File & { file: File }
): Promise<ParsedCam> {
  try {
    const buffer = await camFile.file.arrayBuffer();
    const data = await withTimeout(
      parseCamInWorker(buffer),
      CAM_PARSE_TIMEOUT_MS
    );
    return data ? { fileId: camFile.id, data } : null;
  } catch {
    return null;
  }
}

/** The dropped design file Job details and Tools read; see `chooseCam`. */
const parseCamDrop = (newFiles: (V2File & { file: File })[]) =>
  chooseCam(newFiles, parseCamFile);

/** Where a file sits inside its tool group, for rollback: the op id and the id
 * of the file after it, so restore re-inserts it at the same spot. */
function opPlacement(
  op: V2Operation | undefined,
  fileId: string
): { opId: string | null; opBeforeFileId: string | null } {
  if (!op) return { opId: null, opBeforeFileId: null };
  const idInOp = op.fileIds.indexOf(fileId);
  return { opId: op.id, opBeforeFileId: op.fileIds[idInOp + 1] ?? null };
}

const HEIC = /\.(heic|heif)$/i;

function withoutHeic(fileList: FileList | File[]): File[] {
  const all = Array.from(fileList);
  const kept = all.filter((f) => !HEIC.test(f.name));
  if (kept.length < all.length) {
    toast.error("HEIC isn't supported yet", {
      description: 'On iPhone: Settings > Camera > Formats > Most Compatible.',
    });
  }
  return kept;
}

/** A dropped File as the wizard's own file record. The controller is a guess off
 * the name's prefix, else the extension (the header hasn't been read yet), so
 * G-code lands `unsure`.
 *
 * The prefix comes off the displayed name: a file downloaded from a post and
 * dropped back in carries one, the column already says which controller it is,
 * and a restored file has it stripped — so keeping it here would make the same
 * file read differently either side of a reload. */
function toDroppedFile(file: File): V2File & { file: File } {
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  const kind = kindOf(ext);
  const { name, prefix } = stripControllerPrefix(file.name);
  const named = CONTROLLERS.find(
    (c) => c.toLowerCase() === prefix.toLowerCase()
  );
  const ctrl = kind === 'gcode' ? (named ?? controllerOf(ext)) : '';
  return {
    id: nextId(),
    name,
    ext,
    kind,
    size: fmtSize(file.size),
    sizeBytes: file.size,
    controller: ctrl,
    badge: ctrl.toUpperCase(),
    preview: URL.createObjectURL(file),
    file,
    unsure: kind === 'gcode',
  };
}

/** Everything under a dropped directory, depth-first. `readEntries` hands back
 * at most 100 entries per call, so it has to be drained until it comes back
 * empty — reading it once silently truncates a big folder. */
async function readEntry(entry: FileSystemEntry): Promise<File[]> {
  if (!entry.isDirectory) {
    const file = await new Promise<File | null>((resolve) => {
      (entry as FileSystemFileEntry).file(resolve, () => resolve(null));
    });
    return file ? [file] : [];
  }

  const reader = (entry as FileSystemDirectoryEntry).createReader();
  const drain = async (): Promise<File[]> => {
    const batch = await new Promise<FileSystemEntry[]>((resolve) => {
      reader.readEntries(resolve, () => resolve([]));
    });
    if (batch.length === 0) return [];
    const [here, rest] = await Promise.all([
      Promise.all(batch.map(readEntry)),
      drain(),
    ]);
    return [...here.flat(), ...rest];
  };
  return drain();
}

/** The dropped files, with any dropped folder walked for the files inside it.
 * `dataTransfer.files` lists a dropped folder as a single zero-byte File, so
 * taking it at face value would upload the folder itself as a junk document. */
async function droppedFiles(transfer: DataTransfer): Promise<File[]> {
  const entries = Array.from(transfer.items)
    .map((item) => item.webkitGetAsEntry())
    .filter((entry): entry is FileSystemEntry => Boolean(entry));

  if (entries.length === 0) return Array.from(transfer.files);
  const nested = await Promise.all(entries.map(readEntry));
  return nested.flat();
}

/** Full-page overlay while files are uploading to S3. Individual per-card
 * spinners can't give the user an overall sense of how far through the batch
 * we are — this shows N of M with a progress bar. */
/** Fills the whole step area with a fixed-height panel while the batch uploads
 * to S3. Rendered in place of the step (FilesStep early-returns it), so it
 * doesn't float or offset — the section it occupies IS the panel. */
function UploadingScreen({ done, total }: { done: number; total: number }) {
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
  return (
    <div className="flex h-full min-h-[440px] w-full flex-col items-center justify-center gap-6">
      <div className="flex items-center gap-3">
        <span className="w-5 h-5 rounded-full border-2 border-info/30 border-t-info animate-spin" />
        <span className="text-xl font-semibold text-heading">
          Uploading your files…
        </span>
      </div>
      <div className="w-full max-w-[380px] px-6">
        <div className="flex justify-between mb-1.5">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            {done} of {total} {total === 1 ? 'file' : 'files'}
          </span>
          <span className="text-[11px] font-semibold tabular-nums text-muted-foreground">
            {pct}%
          </span>
        </div>
        <div className="h-1.5 rounded-full bg-muted overflow-hidden">
          <div
            className="h-full rounded-full bg-info transition-[width] duration-300 ease-out-strong"
            style={{ width: `${Math.max(pct, 3)}%` }}
          />
        </div>
        <p className="text-xs text-muted-foreground mt-2 text-center">
          Saving to the cloud — you can keep working once it finishes
        </p>
      </div>
    </div>
  );
}

const TIPS = [
  'Upload all related files in one go',
  'Include finished project photos',
  'Include setup or instructions (PDF)',
  'Filefinity will sort automatically',
  'Choose a thumbnail that best represents the project',
];

function TipsCard() {
  return (
    <div
      data-focus="tips"
      className="w-full md:w-auto md:flex-none text-left rounded-xl border bg-card p-4"
    >
      <h3 className="font-primary text-sm font-semibold mb-2">
        Tips for best results
      </h3>
      <ul className="space-y-1">
        {TIPS.map((tip) => (
          <li key={tip} className="flex items-start gap-2.5 text-sm">
            <Check className="w-4 h-4 mt-0.5 flex-none text-success" />
            <span className="text-muted-foreground">{tip}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Inline loading state while G-code parses. Stays in the page flow so the user
 * can still see the file list and other sections above/below it. */
function ParsingScreen({
  count,
  progress,
}: {
  count: number;
  progress: number;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-20 gap-5">
      <div className="flex items-center gap-3">
        <span className="w-5 h-5 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
        <span className="text-lg font-semibold text-heading">
          Reading your G-code…
        </span>
      </div>

      <div className="w-full max-w-[360px]">
        <div className="flex justify-between mb-1.5">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            {count} {count === 1 ? 'file' : 'files'}
          </span>
          <span className="text-[11px] font-semibold tabular-nums text-muted-foreground">
            {progress}%
          </span>
        </div>
        <div className="h-1.5 rounded-full bg-muted overflow-hidden">
          <div
            className="h-full rounded-full bg-primary transition-[width] duration-300 ease-out-strong"
            style={{ width: `${Math.max(progress, 5)}%` }}
          />
        </div>
        <p className="text-xs text-muted-foreground mt-2 text-center">
          Parsing controller, toolpath, speeds and feeds…
        </p>
      </div>
    </div>
  );
}

/** The drop target and the Select files/folder buttons. Only rendered while not
 * uploading (the loading screen replaces the step then), so nothing here needs
 * to guard on it. */
function DropZone({
  dragOver,
  setDragOver,
  onDrop,
  onPick,
  onClearAll,
  hasFiles,
  fileInput,
  folderInput,
  bad,
  missing,
}: {
  dragOver: boolean;
  setDragOver: (v: boolean) => void;
  onDrop: (e: React.DragEvent<HTMLDivElement>) => void;
  onPick: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClearAll: () => void;
  hasFiles: boolean;
  fileInput: React.RefObject<HTMLInputElement>;
  folderInput: React.RefObject<HTMLInputElement>;
  bad: boolean;
  missing: Missing[];
}) {
  const tone = () => {
    if (bad) return 'border-destructive bg-red-100';
    if (dragOver) return 'border-primary bg-green-100 scale-[1.005]';
    return 'border-primary/40 bg-green-50';
  };
  return (
    <div
      data-focus="dropzone"
      data-invalid={bad || undefined}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      className={cn(
        'border-2 border-dashed rounded-xl p-4 transition-[background-color,border-color,opacity,transform] duration-fast',
        tone()
      )}
    >
      {/* Capped and centred: the drop target is the full width of the step, but
          letting the two halves spread across it puts them on opposite edges. */}
      <div className="mx-auto flex w-full max-w-[820px] flex-col md:flex-row items-center gap-6">
        <div className="flex flex-col items-center gap-2 text-center md:flex-1">
          <span className="text-xl">⇪</span>
          <span className="font-semibold text-sm">
            Drop files here, or choose below
          </span>
          <span className="text-xs font-tertiary text-muted-foreground">
            any format — we&apos;ll figure out what&apos;s what
          </span>

          {/* What "any format" means, in the marks each section uses for it. */}
          <div className="mt-1">
            <AcceptedFormatsCluster />
          </div>

          <div className="flex items-center gap-3 mt-2" data-focus="pick">
            <Button onClick={() => fileInput.current?.click()}>
              Select files
            </Button>
            <Button
              variant="outline"
              onClick={() => folderInput.current?.click()}
            >
              Select folder
            </Button>
          </div>

          {hasFiles && (
            <Button
              variant="outline"
              size="sm"
              className="mt-1 gap-1.5 border-destructive-subtle/25 text-destructive-subtle hover:bg-destructive-subtle/10 hover:text-destructive-subtle"
              onClick={onClearAll}
            >
              <Trash2 className="w-3.5 h-3.5" />
              Remove every file
            </Button>
          )}
        </div>

        <TipsCard />
      </div>

      {/* Two hidden inputs sit here and only differ by `webkitdirectory`, which
          a post-mount effect adds to the folder one — so at first paint they are
          indistinguishable and `input[type=file]` matches both. The testid is
          how a test picks the file one without a strict-mode violation. */}
      <input
        ref={fileInput}
        type="file"
        multiple
        data-testid="v2-file-input"
        className="hidden"
        onChange={onPick}
      />
      {/* A folder pick hands back every file inside it — the same FileList the
          other input gives. Each file's kind still comes from its extension, so
          nothing downstream cares which button was used. */}
      <input
        ref={folderInput}
        type="file"
        multiple
        className="hidden"
        onChange={onPick}
      />

      {bad && (
        <p className="mt-3 text-center text-xs font-medium text-destructive">
          Add {formatList(missing.map((m) => m.label))}
        </p>
      )}
    </div>
  );
}

/** The G-code area: the parse progress while reading, then the controller
 * columns once done. */
function GcodeSection({
  gcodeCount,
  parsing,
  parseProgress,
  files,
  onClear,
  onDelete,
  ...add
}: {
  gcodeCount: number;
  parsing: boolean;
  parseProgress: number;
  files: V2File[];
  onClear: () => void;
  onDelete: (fileId: string) => void;
} & Omit<SectionAddProps, 'accept'>) {
  if (parsing)
    return <ParsingScreen count={gcodeCount} progress={parseProgress} />;
  return (
    <div
      data-focus="controllers"
      className="animate-in fade-in duration-base ease-out-strong fill-mode-both motion-reduce:animate-none"
    >
      <ControllerColumns
        files={files}
        onClear={onClear}
        onDeleteFile={onDelete}
        accept={ACCEPT_BY_KIND.gcode}
        {...add}
      />
    </div>
  );
}

/** The sorted files below the drop zone: photos, G-code and CAD/docs, each with
 * its own clear/delete. Always shown, so an empty section reads as one to fill. */
function FileSections({
  files,
  images,
  docFiles,
  gcodeCount,
  parsing,
  parseProgress,
  thumbnailFileId,
  clearKind,
  deleteFile,
  onSelectThumb,
  onAddFiles,
  onDropFiles,
}: {
  files: V2File[];
  images: V2File[];
  docFiles: V2File[];
  gcodeCount: number;
  parsing: boolean;
  parseProgress: number;
  thumbnailFileId: string | null;
  clearKind: (kind: V2File['kind'] | 'all') => void;
  deleteFile: (fileId: string) => void;
  onSelectThumb: (fileId: string) => void;
  onAddFiles: (files: FileList) => void;
  onDropFiles: (e: React.DragEvent<HTMLDivElement>) => void;
}) {
  const { camExtracted, camExtractedFileId, camParsing } =
    useSelector(selectV2);
  const [openCamId, setOpenCamId] = useState<string | null>(null);
  const add = { onAddFiles, onDropFiles };
  return (
    <div className="mt-4 space-y-4">
      {/* Photos lead: the thumbnail is the one thing here that decides how the
          project looks in the feed, and it is picked by eye — so the grid gets
          the full width rather than half of a paired row. */}
      <div data-focus="images">
        <ImageGrid
          images={images}
          thumbnailFileId={thumbnailFileId}
          onSelect={onSelectThumb}
          onClear={() => clearKind('image')}
          onDeleteFile={deleteFile}
          accept={ACCEPT_BY_KIND.image}
          {...add}
        />
      </div>
      {/* Then G-code: the file the step exists for, the one the parser reads
          every job field out of, and the only one whose column matters. */}
      <GcodeSection
        gcodeCount={gcodeCount}
        parsing={parsing}
        parseProgress={parseProgress}
        files={files}
        onClear={() => clearKind('gcode')}
        onDelete={deleteFile}
        {...add}
      />
      <div data-focus="docs">
        <DocLists
          files={docFiles}
          onClear={() => clearKind('doc')}
          onOpenFile={setOpenCamId}
          onDeleteFile={deleteFile}
          accept={ACCEPT_BY_KIND.doc}
          {...add}
        />
      </div>

      <CamDetailModal
        file={docFiles.find((f) => f.id === openCamId) ?? null}
        extracted={camDataFor(openCamId, camExtracted, camExtractedFileId)}
        parsing={camParsing}
        onClose={() => setOpenCamId(null)}
      />
    </div>
  );
}

export default function FilesStep() {
  const dispatch = useDispatch();
  const {
    files,
    parsing,
    camParsing,
    parseProgress,
    uploadProgress,
    uploadTotal,
    thumbnailFileId,
    operations,
    extractedData,
    orderConfirmed,
  } = useSelector(selectV2);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  // In-flight server deletes. Continue/Back stay blocked while any is pending —
  // navigating away mid-delete would let a rollback fire on another step, or
  // race the next step's sync.
  const [deletingCount, setDeletingCount] = useState(0);
  const deleting = deletingCount > 0;
  const { syncFiles } = useDraftSync();
  const [deleteAttachment] = useDeleteAttachmentMutation();

  // `webkitdirectory` is what turns a file input into a folder picker. It isn't
  // in React's prop types, so it goes on after mount.
  const fileInput = useRef<HTMLInputElement>(null);
  const folderInput = useRef<HTMLInputElement>(null);
  useEffect(() => {
    folderInput.current?.setAttribute('webkitdirectory', '');
  });
  const missing = missingForFiles(files, orderConfirmed);
  const { bad, guard } = useFieldErrors(missing);

  // Files upload from this step because a G-code file's uploaded name embeds
  // its controller, which the user finalises in the columns above.
  const handleContinue = async () => {
    if (!guard()) return;
    setUploading(true);
    const ok = await syncFiles();
    setUploading(false);
    if (ok) dispatch(setStep(3));
  };

  const images = files.filter((f) => f.kind === 'image');
  const gcodeFiles = files.filter((f) => f.kind === 'gcode');
  const docFiles = files.filter((f) => f.kind === 'doc');
  const gcodeCount = gcodeFiles.length;

  // fallow-ignore-next-line complexity
  const handleFiles = async (fileList: FileList | File[]) => {
    const newFiles = withoutHeic(fileList).map(toDroppedFile);
    if (!newFiles.length) return;

    dispatch(addFiles(newFiles));
    dispatch(setThumbnailFileId(nextThumbnailId(newFiles, thumbnailFileId)));

    // Every G-code file is parsed, so each card can show its own real
    // toolpath. One file's metadata drives the wizard's fields.
    const newGcodeFiles = newFiles.filter((f) => f.kind === 'gcode');
    const [gcodeFile] = newGcodeFiles;
    if (gcodeFile) {
      dispatch(setParsing(true));
      dispatch(setParseProgress(5));
      try {
        const parsed = await Promise.all(
          newGcodeFiles.map(async (f) => {
            try {
              const text = await f.file.text();
              return { id: f.id, result: await parseGcodeInWorker(text) };
            } catch {
              return { id: f.id, result: null };
            }
          })
        );

        dispatch(setParseProgress(40));
        dispatch(
          setFileToolpaths(
            Object.fromEntries(
              parsed.map((p) => [p.id, p.result?.toolpathD ?? null])
            )
          )
        );
        dispatch(
          setFileExtracted(
            Object.fromEntries(parsed.map((p) => [p.id, p.result ?? null]))
          )
        );

        dispatch(sortGcodeByCutOrder());

        dispatch(setParseProgress(60));

        // The controller comes from the file's header, not its extension —
        // .nc and .gcode are each shared by two controllers. A file whose
        // header names nothing keeps the extension's guess for the user to fix.
        parsed.forEach((p) => {
          const detected = p.result?.controllerType;
          if (detected) {
            dispatch(
              updateFileController({ fileId: p.id, controller: detected })
            );
          }
        });

        const result = primaryGcodeParse(parsed.map((p) => p.result));
        if (!result) throw new Error('Could not read that G-code file');

        dispatch(setParseProgress(80));
        dispatch(setExtractedData(result));

        dispatch(setParseProgress(100));
        dispatch(setParsedReady(true));
      } catch {
        // Best-effort
      } finally {
        dispatch(setParsing(false));
      }
    }

    // The .crv/.art carries stock size, software and — for Carveco — the tool
    // and speed of every operation. Parsed independently of the G-code: a
    // failure there never touches the G-code pre-fill, and a CAM-only drop
    // works with no G-code present at all. Runs after the G-code block so the
    // await here can't leave `parsing` false while G-code is still mid-parse,
    // which would let Continue through before the controller/pre-fill settles.
    //
    // Gated on its own `camParsing` flag, separate from `parsing`: the G-code
    // block above already cleared `parsing` in its own `finally` before this
    // point, so `parsing` alone can't hold Continue for this await. Without a
    // flag of its own, the user could reach Tools before the CAM result lands
    // — and `seedOperationsFromSources` only ever seeds once, so a CAM result
    // that arrives after that has nothing left to land in.
    if (newFiles.some((f) => isCamFile(f.name))) {
      dispatch(setCamParsing(true));
      try {
        const cam = await parseCamDrop(newFiles);
        if (cam) {
          dispatch(setCamExtracted(cam.data));
          dispatch(setCamExtractedFileId(cam.fileId));
        }
      } finally {
        dispatch(setCamParsing(false));
      }
    }
  };

  const onDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    if (uploading) return;
    const dropped = await droppedFiles(e.dataTransfer);
    if (dropped.length) handleFiles(dropped);
  };

  /** Everything removeFile/clearFiles strips off a file — its slot in the list,
   * its spot in a tool group, whether it's the thumbnail, and the parser output
   * it may have been the last to keep alive — captured so a failed server delete
   * can be rolled back exactly. Read the closure's pre-delete state, so it must
   * be called before the optimistic dispatch. */
  const snapshotFile = (file: V2File) => {
    const op = operations.find((o) => o.fileIds.includes(file.id));
    return {
      file,
      index: files.findIndex((f) => f.id === file.id),
      ...opPlacement(op, file.id),
      wasThumbnail: thumbnailFileId === file.id,
      extractedData,
    };
  };

  const clearKind = async (kind: V2File['kind'] | 'all') => {
    const doomed = files.filter((f) => kind === 'all' || f.kind === kind);
    if (doomed.length === 0) return;
    // Snapshot every file first, against the pre-clear list, then remove them.
    const snapshots = doomed.map(snapshotFile);
    dispatch(clearFiles(kind));

    const uploaded = doomed
      .map((f, i) => ({ f, snap: snapshots[i] }))
      .filter(({ f }) => f.attachmentId);
    if (uploaded.length === 0) return;

    setDeletingCount((n) => n + 1);
    let anyFailed = false;
    await Promise.all(
      uploaded.map(async ({ f, snap }) => {
        try {
          await deleteAttachment({
            attachmentId: f.attachmentId as string,
          }).unwrap();
        } catch {
          dispatch(restoreFile(snap));
          anyFailed = true;
        }
      })
    );
    setDeletingCount((n) => n - 1);
    if (anyFailed) {
      toast.error('Some files could not be removed', {
        description: "They've been put back — try again.",
      });
    }
  };

  const deleteFile = async (fileId: string) => {
    const file = files.find((f) => f.id === fileId);
    if (!file) return;
    // Optimistic: drop it now, put it back only if the server delete fails.
    const snapshot = snapshotFile(file);
    dispatch(removeFile(fileId));
    if (!file.attachmentId) return;

    setDeletingCount((n) => n + 1);
    try {
      await deleteAttachment({ attachmentId: file.attachmentId }).unwrap();
    } catch {
      dispatch(restoreFile(snapshot));
      toast.error("Could not remove file — it's been put back");
    } finally {
      setDeletingCount((n) => n - 1);
    }
  };

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      handleFiles(e.target.files);
    }
    // eslint-disable-next-line no-param-reassign
    e.target.value = '';
  };

  // While uploading, the loading panel replaces the step outright — it takes the
  // whole section at a fixed height, so nothing else is on screen to interact
  // with (or to shift the panel around) until the batch finishes.
  if (uploading) {
    return <UploadingScreen done={uploadProgress} total={uploadTotal} />;
  }

  return (
    <div className="flex flex-col min-h-full">
      <h2 className={STEP_HEADING}>Add your project files</h2>
      <p className="text-sm text-muted-foreground mb-4 max-w-[560px]">
        Drop G-code, photos, CAD and PDFs together — we sort them and start
        reading your G-code right away.
      </p>

      <DropZone
        dragOver={dragOver}
        setDragOver={setDragOver}
        onDrop={onDrop}
        onPick={onPick}
        onClearAll={() => clearKind('all')}
        hasFiles={files.length > 0}
        fileInput={fileInput}
        folderInput={folderInput}
        bad={bad('dropzone')}
        missing={missing}
      />

      <FileSections
        files={files}
        images={images}
        docFiles={docFiles}
        gcodeCount={gcodeCount}
        parsing={parsing}
        parseProgress={parseProgress}
        thumbnailFileId={thumbnailFileId}
        clearKind={clearKind}
        deleteFile={deleteFile}
        onSelectThumb={(fileId) => dispatch(setThumbnailFileId(fileId))}
        onAddFiles={handleFiles}
        onDropFiles={onDrop}
      />

      {/* Outside the files block: a link isn't a file, and it can be added to a
          project that has none yet. */}
      <div data-focus="videos" className="mt-4">
        <VideoLinks />
      </div>

      {/* Continue is blocked while the G-code parses (its uploaded name embeds
          the controller the parse decides) and while a delete is in flight;
          Back only by the delete. */}
      <WizardFooter
        onBack={() => dispatch(setStep(1))}
        onContinue={handleContinue}
        busy={parsing}
        busyLabel="Reading G-code…"
        backDisabled={deleting}
        // camParsing as well as parsing: the g-code parse clears its own flag
        // before the CAM parse it awaits has started
        continueDisabled={parsing || camParsing || deleting}
      />
    </div>
  );
}
